import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quantService from '../../../services/quantService';

const AuditPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [trials, setTrials] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    quantService.listDiscovery()
      .then(({ data }) => {
        setJobs(data || []);
        if (data?.length && !selectedJob) setSelectedJob(data[0].job_id);
      })
      .catch(e => setError(e?.response?.data?.detail || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    quantService.getTrials(selectedJob)
      .then(({ data }) => setTrials(data || []))
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, [selectedJob]);

  // Trial type breakdown
  const breakdown = trials.reduce((acc, t) => {
    acc[t.trial_type] = (acc[t.trial_type] || 0) + 1;
    return acc;
  }, {});

  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-3">
          Discovery Job
        </h2>
        <select value={selectedJob || ''} onChange={e => setSelectedJob(e.target.value)}
                className="w-full md:w-auto px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded">
          {jobs.map(j => (
            <option key={j.job_id} value={j.job_id}>
              {j.job_id.slice(0, 8)}… ({j.universe_name}, {j.status})
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Trial Log Summary
          </h2>
          <span className="text-sm font-mono text-gray-500">{trials.length.toLocaleString()} trials</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(breakdown).map(([type, count]) => (
            <div key={type} className="bg-gray-50 dark:bg-gray-800/50 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">{type}</div>
              <div className="text-base font-mono">{count.toLocaleString()}</div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-400 mt-3">
          DSR uses max(1400, n_trials_logged) — preventing under-counting from partial logging failures.
          Each ElasticNet CV iteration, RF fold, and label-horizon scan is logged here.
        </p>
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Recent Trials
          </h2>
        </div>
        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Parameters</th>
              </tr>
            </thead>
            <tbody>
              {trials.slice(0, 200).map(t => (
                <tr key={t.trial_id} className="border-t border-gray-100 dark:border-gray-800/50">
                  <td className="px-4 py-1 text-xs font-mono text-gray-500">
                    {new Date(t.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-1 text-xs">{t.trial_type}</td>
                  <td className="px-4 py-1 text-xs font-mono text-gray-500 truncate max-w-[400px]">
                    {JSON.stringify(t.parameters)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditPage;
